import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
} from "utils/utils";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import s from "./Approved.module.scss";
import { queryList, rejectDebitNotes } from "./model";
import { queryTableData } from "../apiUtils";

const Approved = ({ path }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "DebitApprovedListGrid";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState({
    id: "",
    merchantId: "",
    applicant: "",
    reviewer: "",
    currencyCode: "",
    debitAmount: "",
    created: null,
  });
  const [focus, setFocus] = useState({});

  const [columnsArray, setColumnsArray] = useState([]);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectDialogData, setRejectDialogData] = useState({});
  const [dialogLoading, setDialogLoading] = useState(false);
  const [openSubTableRow, setOpenSubTableRow] = useState(NaN);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse == undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const handleQueryListUIResponse = (apiResponse) => {
    if (!apiResponse || !apiResponse?.data) {
      return snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.debit.sectionName")}` }), "error");
    }
    const { status, data: responseBody } = apiResponse;

    if (status === 401) {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    }

    if (status === 403) {
      return snackbar(t("general.message.unauthorized"), "error");
    }

    if (responseBody.responseCode === "0000") {
      setDatasource(responseBody);
      setCurrentPage(responseBody.page);
      setPageSize(responseBody.size);
      Object.assign(pageSizeInfoRef.current, {
        currentpage: responseBody.page,
        pagesize: responseBody.size,
      });
    } else if (responseBody.responseCode === "2408") {
      snackbar(t("general.message.no.result"), "warning");
      setDatasource({ data: [], page: 0, total: 0 });
      setCurrentPage(0);
      setPageSize(20);
      Object.assign(pageSizeInfoRef.current, {
        currentpage: 0,
        pagesize: 20,
      });
    } else {
      snackbar(t("general.message.networkError"), "error");
    }
  };

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryTableData(queryList, {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    handleQueryListUIResponse(apiResponse);
    setLoading(false);
    setSearch(false);
  };

  const resetInputField = () => {
    setSearchFieldInput({
      id: "",
      merchantId: "",
      applicant: "",
      reviewer: "",
      currencyCode: "",
      debitAmount: "",
      created: null,
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiReponse = await queryTableData(queryList, payload);
    handleQueryListUIResponse(apiReponse);
    setSearch(true);
    setSearching(false);
  };

  const openRejectDialog = (data) => {
    setIsRejectDialogOpen(true);
    setDialogLoading(false);
    return setRejectDialogData(data);
  };

  const closeRejectDialog = () => {
    setDialogLoading(false);
    return setIsRejectDialogOpen(false);
  };

  const confirmReject = async () => {
    const payload = { ids: [rejectDialogData.id] };
    setDialogLoading(true);
    const apiResponse = await rejectDebitNotes(payload);
    closeRejectDialog();

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.reject.function.success.message"),
              "success",
            );
            return handleSearch();
          } else {
            snackbar(
              t("general.message.reject.function.fail.message"),
              "error",
            );
          }
        }
      }
    }
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const createdOnChange = (e) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    setSearchFieldInput({ ...searchFieldInput, created: date });
    return;
  };

  const handleOpenSubTable = (id) => {
    if (openSubTableRow === id) {
      setOpenSubTableRow(NaN);
    } else {
      setOpenSubTableRow(id);
    }
  };

  const subTableColumns = [
    {
      index: 0,
      key: "id",
      title: t("page.finance.debit.approved.list.subtable.column.title.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "currencyCode",
      title: t(
        "page.finance.debit.approved.list.subtable.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "debitAmount",
      title: t(
        "page.finance.debit.approved.list.subtable.column.title.debit.amount",
      ),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "settlePlanId",
      title: t(
        "page.finance.debit.approved.list.subtable.column.title.settlement.plan.id",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
  ];

  const columns = [
    {
      index: 0,
      key: "id",
      title: t("page.finance.debit.approved.list.column.title.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "merchantId",
      title: t("page.finance.debit.approved.list.column.title.merchant.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "applicant",
      title: t("page.finance.debit.approved.list.column.title.applicant"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "reviewer",
      title: t("page.finance.debit.approved.list.column.title.reviewer"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "currencyCode",
      title: t(
        "page.finance.debit.approved.list.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "debitAmount",
      title: t("page.finance.debit.approved.list.column.title.debit.amount"),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 6,
      key: "created",
      title: t("page.finance.debit.approved.list.column.title.created"),
      sorter: false,
      render: (data, key) => {
        return moment(data[key]).format("YYYY-MM-DD");
      },
    },
    {
      index: 7,
      key: "reason",
      title: t("page.finance.debit.approved.list.column.title.reason"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 9,
      key: "action",
      title: t("page.finance.debit.approved.list.column.title.action"),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <button onClick={() => openRejectDialog(data)}>
              {t("page.finance.debit.approved.list.column.title.action.1")}
            </button>
          </div>
        );
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <OutlinedText
            label={t("page.finance.debit.approved.list.id.label")}
            value={searchFieldInput.id}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "id")}
            onfocusFn={() => textInputOnFocus("id")}
            onblurFn={() => textInputOnBlur("id")}
            sxParams={{ textFocus: focus.id }}
          />
          <OutlinedText
            label={t("page.finance.debit.approved.list.merchant.id.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
          />
          <OutlinedText
            label={t("page.finance.debit.approved.list.applicant.label")}
            value={searchFieldInput.applicant}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "applicant")}
            onfocusFn={() => textInputOnFocus("applicant")}
            onblurFn={() => textInputOnBlur("applicant")}
            sxParams={{ textFocus: focus.applicant }}
          />
          <OutlinedText
            label={t("page.finance.debit.approved.list.reviewer.label")}
            value={searchFieldInput.reviewer}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "reviewer")}
            onfocusFn={() => textInputOnFocus("reviewer")}
            onblurFn={() => textInputOnBlur("reviewer")}
            sxParams={{ textFocus: focus.reviewer }}
          />
          <Selection
            label={t(
              "page.finance.debit.approved.list.transaction.currency.label",
            )}
            selectionValue={searchFieldInput.currencyCode}
            onchangeFn={(e) => textInputOnchange(e, "currencyCode")}
            onfocusFn={() => textInputOnFocus("currencyCode")}
            onblurFn={() => textInputOnBlur("currencyCode")}
            options={transactionCurrencyOptions("all")}
            sxParams={{
              selectionFocus: focus.currencyCode,
            }}
          />
          <OutlinedText
            label={t("page.finance.debit.approved.list.debit.amount.label")}
            value={searchFieldInput.debitAmount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "debitAmount")}
            onfocusFn={() => textInputOnFocus("debitAmount")}
            onblurFn={() => textInputOnBlur("debitAmount")}
            sxParams={{ textFocus: focus.debitAmount }}
          />
          <Datepicker
            label={t("page.finance.debit.approved.list.created.label")}
            errorMessage={t(
              "page.finance.debit.approved.list.created.error.message",
            )}
            type="start"
            value={searchFieldInput.created}
            onchangeFn={createdOnChange}
            onfocusFn={() => textInputOnFocus("created")}
            onblurFn={() => textInputOnBlur("created")}
            sxParams={{ datepickerFocus: focus.created }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading || searching}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "finance-debit-notes-approved",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
              subTable
              subTableDisplayRow={openSubTableRow}
              handleOpenSubTableRow={handleOpenSubTable}
              subTableColumn={subTableColumns}
              subTableContent={"debitList"}
            />
          )}

          <SimpleDialog
            open={isRejectDialogOpen}
            dialogOncloseFn={closeRejectDialog}
            title={t(
              "page.finance.debit.approved.list.reject.function.dialog.title",
            )}
            content={t(
              "page.finance.debit.approved.list.reject.function.dialog.content",
            )}
            confirmFn={confirmReject}
            positiveAction={t("component.simple.dialog.action.3")}
            negativeAction={t("component.simple.dialog.action.4")}
            negativeActionDisabled={dialogLoading}
            positiveActionLoading={dialogLoading}
          />
          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Approved);
